export type AlertType = 'price_anomaly' | 'size_not_comparable';
export type AlertFilter = 'all' | 'price_anomaly' | 'size_not_comparable' | 'no_alerts';
export type CandidateFilter = 'all' | 'with_candidates' | 'zero_candidates';
export type MatchType = 'machine' | 'human';
export type SortDirection = 'asc' | 'desc';
export type SortField = 'sku' | 'brand' | 'name' | 'modifiedAt' | 'humanDecidedAt';

export interface AlertInfo {
  type: AlertType;
  details?: string;
}

export interface NormalizationSettings {
  competitorSize: string;
  competitorPack: string;
  ourSize: string;
  ourPack: string;
  comparable?: boolean;
  computedRatio: number;
  /** Absent when the source carries no price; readers already fall back rather than assume a number. */
  normalizedPrice?: number | undefined;
}

export interface SkinSeoulProduct {
  id?: string;
  sku: string;
  name: string;
  brand: string;
  category: string[];
  size: string;
  pack: string;
  productImageUrls?: string[];
  productLink?: string;
  type?: string;
  originalPrice?: number;
  salePrice?: number;
  currency?: string;
  modifiedAt?: string;
  cogs?: number;
  variations?: SkinSeoulProduct[];
}

export interface PriceInfo {
  currency: string;
  originalPrice?: number;
  salesPrice?: number;
}

export interface CompetitorProduct {
  sku: string;
  name: string;
  brand: string;
  productLink: string;
  category: string;
  size: string;
  pack: string;
  modifiedAt: string | null;
  productImageUrls?: string;
  normalizedName: string;
  possibility: number;
  type?: string;
  price?: PriceInfo[];
  originalPrice?: number;
  salePrice?: number;
  currency?: string;
  matchType?: MatchType;
  normalizationRatio?: number;
  /** Explicitly undefined when the source carries no price — `exactOptionalPropertyTypes` needs that spelled out. */
  normalizedPrice?: number | undefined;
  normalization?: NormalizationSettings;
  alerts?: AlertInfo[];
}

export interface Competitor {
  competitor: string;
  competitorProducts: CompetitorProduct[];
}

export interface ProductData {
  skinSeoulProduct: SkinSeoulProduct;
  machineMatchedProducts: Competitor[];
  humanMatchedProducts?: Competitor[];
  humanVerifiedNoCompetitor?: boolean;
  humanDecidedBy?: string;
  humanDecidedAt?: string;
  alerts?: AlertInfo[];
}

export interface MatchedProductWithAlerts {
  product: ProductData;
  alerts: string[];
}

export interface ListMatchedProductsResponse {
  items: MatchedProductWithAlerts[];
  limit: number;
  lastEvaluatedKey?: string | null;
}

export interface ProductFiltersState {
  search: string;
  alertType: AlertType | 'all';
  noAlerts: boolean;
}

export interface SortState {
  field: SortField;
  direction: SortDirection;
}

export interface PaginationState {
  limit: number;
  lastEvaluatedKey?: string | null;
}

export interface SelectedProductMap {
  [skinSeoulSku: string]: {
    [competitor: string]: string; // Maps competitor name to selected competitor product SKU
  };
}

// Parse JSON strings to arrays where needed
export const parseJsonArray = (jsonString: string | undefined): string[] => {
  if (!jsonString) {
    return [];
  }
  try {
    return JSON.parse(jsonString);
  } catch (e) {
    console.error('Error parsing JSON:', e);
    return [];
  }
};

export const parseSize = (size: string | undefined): number => {
  if (!size) return 0;
  const match = size.match(/(\d+(?:\.\d+)?)/);
  return match && match[1] ? parseFloat(match[1]) : 0;
};

export const parsePack = (pack: string | undefined): number => {
  if (!pack) return 1;
  const match = pack.match(/(\d+)/);
  return match && match[1] ? parseInt(match[1], 10) : 1;
};

export const computeNormalizationRatio = (
  competitorSize: string,
  competitorPack: string,
  ourSize: string,
  ourPack: string,
): number => {
  const compSize = parseSize(competitorSize);
  const compPack = parsePack(competitorPack);
  const ssSize = parseSize(ourSize);
  const ssPack = parsePack(ourPack);

  if (ssSize === 0 || ssPack === 0) return 1;
  const compTotal = compSize * compPack;
  const ssTotal = ssSize * ssPack;
  return ssTotal > 0 ? compTotal / ssTotal : 1;
};

export const selectMatchesInUse = (product: ProductData): Competitor[] => {
  const humanMatches = (product.humanMatchedProducts || []).filter(
    (competitor) => competitor.competitorProducts.length > 0,
  );
  return humanMatches.length > 0 ? humanMatches : product.machineMatchedProducts;
};

export const hasCompetitorCandidates = (product: ProductData): boolean =>
  selectMatchesInUse(product).some((competitor) => competitor.competitorProducts.length > 0);

export const skuEndsWithDigit = (sku: string, lastDigit: string): boolean => {
  if (!lastDigit) {
    return true;
  }
  return (sku || '').trim().endsWith(lastDigit);
};

// Words that name a product category rather than distinguish one variant from another.
const NON_DISTINGUISHING_WORDS = new Set(
  (
    'the a an and for with set of pack ml g kg oz ea pcs pc sheet sheets mask cream serum toner essence ' +
    'lotion gel oil sun spf pa balm foam cleanser water pad pads patch patches ampoule emulsion mist stick soap shampoo new'
  ).split(' '),
);

const MINIMUM_DISTINGUISHING_DIFFERENCE = 4;

const distinguishingWords = (productName: string): Set<string> =>
  new Set(
    (productName || '')
      .toLowerCase()
      .match(/[a-z0-9]+/g)
      ?.filter((word) => !NON_DISTINGUISHING_WORDS.has(word)) ?? [],
  );

/** A reviewer-facing hint only, never an input to ranking or selection. */
export const hasVariantMismatch = (ourProductName: string, competitorProductName: string): boolean => {
  const ourWords = distinguishingWords(ourProductName);
  const theirWords = distinguishingWords(competitorProductName);
  if (ourWords.size === 0 || theirWords.size === 0) {
    return false;
  }

  const wordsOnlyOnOurSide = [...ourWords].filter((word) => !theirWords.has(word));
  const wordsOnlyOnTheirSide = [...theirWords].filter((word) => !ourWords.has(word));
  if (wordsOnlyOnOurSide.length === 0 || wordsOnlyOnTheirSide.length === 0) {
    return false;
  }

  return wordsOnlyOnOurSide.length + wordsOnlyOnTheirSide.length >= MINIMUM_DISTINGUISHING_DIFFERENCE;
};

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
const DAYS_PER_MONTH = 30;
const DAYS_PER_YEAR = 365;

/** Reviewers act on the age of a decision, not its exact timestamp, so this reads in whole units. */
export const formatDecisionAge = (decidedAt: string, now: Date = new Date()): string => {
  const decidedTime = Date.parse(decidedAt);
  if (Number.isNaN(decidedTime)) {
    return '';
  }

  const elapsedDays = Math.floor((now.getTime() - decidedTime) / MILLISECONDS_PER_DAY);
  if (elapsedDays < 1) {
    return 'today';
  }
  if (elapsedDays === 1) {
    return 'yesterday';
  }
  if (elapsedDays < DAYS_PER_MONTH) {
    return `${elapsedDays} days ago`;
  }

  if (elapsedDays < DAYS_PER_YEAR) {
    const elapsedMonths = Math.floor(elapsedDays / DAYS_PER_MONTH);
    return elapsedMonths === 1 ? '1 month ago' : `${elapsedMonths} months ago`;
  }

  const elapsedYears = Math.floor(elapsedDays / DAYS_PER_YEAR);
  return elapsedYears === 1 ? 'over a year ago' : `over ${elapsedYears} years ago`;
};

/** The queue lists parents and standalone products only; a variation is shown under its parent, never as its own card. */
export const selectNonVariationProducts = (products: ProductData[]): ProductData[] =>
  products.filter((product) => product.skinSeoulProduct.type !== 'Variation');

export const reviewersOf = (products: ProductData[]): string[] => {
  const namedReviewers = products
    .map((product) => product.humanDecidedBy)
    .filter((reviewer): reviewer is string => Boolean(reviewer));
  return [...new Set(namedReviewers)].sort();
};

export const sortProducts = (products: ProductData[], sort: SortState): ProductData[] => {
  const direction = sort.direction === 'asc' ? 1 : -1;

  return [...products].sort((first, second) => {
    if (sort.field === 'modifiedAt') {
      const firstTime = Date.parse(first.skinSeoulProduct.modifiedAt || '') || 0;
      const secondTime = Date.parse(second.skinSeoulProduct.modifiedAt || '') || 0;
      return (firstTime - secondTime) * direction;
    }

    if (sort.field === 'humanDecidedAt') {
      const firstTime = Date.parse(first.humanDecidedAt || '') || 0;
      const secondTime = Date.parse(second.humanDecidedAt || '') || 0;
      return (firstTime - secondTime) * direction;
    }

    const firstValue = first.skinSeoulProduct[sort.field] || '';
    const secondValue = second.skinSeoulProduct[sort.field] || '';
    return firstValue.localeCompare(secondValue, undefined, { numeric: true, sensitivity: 'base' }) * direction;
  });
};

export const detectAlerts = (
  product: SkinSeoulProduct,
  competitorProduct: CompetitorProduct,
): AlertInfo[] => {
  const alerts: AlertInfo[] = [];
  const cogs = product.cogs || 0;
  const competitorPrice = competitorProduct.originalPrice || competitorProduct.salePrice || 0;

  if (competitorPrice > 0 && cogs > 0) {
    const upperBound = 1.5 * cogs * 0.00092 / 0.5;
    const lowerBound = 0.5 * cogs * 0.00092 / 0.5;
    if (competitorPrice > upperBound || competitorPrice < lowerBound) {
      alerts.push({
        type: 'price_anomaly',
        details: `Price ${competitorPrice} outside bounds [${lowerBound.toFixed(2)}, ${upperBound.toFixed(2)}]`,
      });
    }
  }

  if (competitorProduct.normalization?.comparable === false) {
    alerts.push({
      type: 'size_not_comparable',
      details: `Size cannot be compared: ours ${product.size || 'N/A'}/${product.pack || '1'} vs theirs ${competitorProduct.size || 'N/A'}/${competitorProduct.pack || '1'}`,
    });
  }

  return alerts;
};
